import {isNullOrUndefined} from '@adsk/bim360-matrix-react-helpers';
import {ActivityQueryParams, ACTIVITY_VERBS} from '../constants/activity';
import {ATTACHMENT_PAGE_LIMIT, ATTACHMENT_STATUS_COMPLETED} from '../constants/attachments';
import {
  ACTIVITIES_PARAMS,
  ATTACHMENT_FILTER_PROPERTIES,
  ISSUE_FILTER_PROPERTIES,
  ISSUE_HIDDEN_FILTER_PROPERTIES,
  PAGE_PARAMS,
  SORTING_PARAMS,
  TEMPLATES_DATE_FILTERS,
  TEMPLATES_FILTER_PROPERTIES,
  TEMPLATES_MULTI_SELECT_FILTERS
} from '../constants/issue-query-params';
import {TEMPLATE_FIELD_NAMES, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES} from '../constants/issue-templates';
import {formatLocalDateTimeRange, formatUtcDateRange} from './date-helpers';
import {getSortValue} from './jsonapi-helpers';

export const buildGetParams = ({targetUrn, documentVersion, limit, offset, queryParams = {}} = {}) => {
  const getQueryParam = param => (param.constructor === Array ? param.join(',') : param);
  const query = {
    limit: limit || 10,
    offset: offset || 0
  };
  const customAttributesQueryParams = queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]
    ? JSON.parse(queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES])
    : [];
  const {sort} = queryParams;
  if (sort) {
    query['sortBy'] = getSortValue(sort, {});
  }
  const setFilter = (name, value) => (query[`filter[${name}]`] = value);
  if (targetUrn) {
    setFilter('linkedDocumentUrn', `${targetUrn}${documentVersion ? `@${documentVersion}` : ''}`);
  }
  Object.entries({
    [ISSUE_HIDDEN_FILTER_PROPERTIES.ID]: 'id',
    [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: 'status',
    [ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER]: 'issueTypeId',
    [ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER]: 'issueSubtypeId',
    [ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]: 'createdBy',
    [ISSUE_FILTER_PROPERTIES.OWNER_FILTER]: 'ownerId',
    [ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]: 'assignedTo',
    [ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER]: 'rootCauseId',
    [ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER]: 'linkedTo'
  }).forEach(([filterName, queryName]) => {
    const param = queryParams[filterName];
    if (param && (!param.hasOwnProperty('length') || param.length > 0)) {
      setFilter(queryName, getQueryParam(param));
    }
  });

  if (queryParams[ISSUE_FILTER_PROPERTIES.SEARCH]) {
    setFilter('search', `~${getQueryParam(queryParams[ISSUE_FILTER_PROPERTIES.SEARCH])}`);
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER] ||
    queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
  ) {
    setFilter(
      'createdAt',
      formatLocalDateTimeRange(
        queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER],
        queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
      )
    );
  }
  if (
    queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER] ||
    queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
  ) {
    setFilter(
      'dueDate',
      formatUtcDateRange(
        queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER],
        queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
      )
    );
  }
  if (queryParams[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]) {
    setFilter(
      queryParams[ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS] ? 'subLocationId' : 'locationId',
      queryParams[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]
    );
  }

  if (Array.isArray(customAttributesQueryParams)) {
    customAttributesQueryParams.forEach(({id, value}) => {
      query[`filter[customAttributes][${id}]`] = typeof value === 'object' ? value.id : value;
    });
  }

  return query;
};

export const buildTemplatesGetParams = queryParams => {
  const getQueryParam = param => (param.constructor === Array ? param.join(',') : param);
  const query = {
    [PAGE_PARAMS.LIMIT]: queryParams[PAGE_PARAMS.LIMIT] || 100,
    [PAGE_PARAMS.OFFSET]: queryParams[PAGE_PARAMS.OFFSET] || 0
  };
  if (queryParams[SORTING_PARAMS.SORT]) {
    query[SORTING_PARAMS.SORT] = queryParams[SORTING_PARAMS.SORT];
  }
  const specialFilters = [...TEMPLATES_DATE_FILTERS, TEMPLATES_FILTER_PROPERTIES.SEARCH];
  Object.values(TEMPLATES_FILTER_PROPERTIES)
    .filter(filterName => !specialFilters.includes(filterName))
    .forEach(filterName => {
      if (TEMPLATES_MULTI_SELECT_FILTERS.includes(filterName)) {
        if (queryParams[filterName] && queryParams[filterName].length) {
          query[`filter[${filterName}]`] = getQueryParam(queryParams[filterName]);
        }
      } else if (!isNullOrUndefined(queryParams[filterName])) {
        query[`filter[${filterName}]`] = queryParams[filterName];
      }
    });
  if (queryParams[TEMPLATES_FILTER_PROPERTIES.SEARCH]) {
    const searchQueryParam = `filter[${TEMPLATE_FIELD_NAMES.ISSUE_ATTRIBUTES}.${
      TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE
    }]`;
    query[searchQueryParam] = queryParams[TEMPLATES_FILTER_PROPERTIES.SEARCH];
  }

  if (
    queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_START] ||
    queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_END]
  ) {
    query[`filter[${TEMPLATE_FIELD_NAMES.CREATED_AT}]`] = formatLocalDateTimeRange(
      queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_START],
      queryParams[TEMPLATES_FILTER_PROPERTIES.CREATED_AT_END]
    );
  }

  if (
    queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_START] ||
    queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_END]
  ) {
    query[`filter[${TEMPLATE_FIELD_NAMES.UPDATED_AT}]`] = formatLocalDateTimeRange(
      queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_START],
      queryParams[TEMPLATES_FILTER_PROPERTIES.UPDATED_AT_END]
    );
  }

  return query;
};

export const buildTemplateCategoriesGetParams = (queryParams = {}) => ({
  [PAGE_PARAMS.LIMIT]: queryParams[PAGE_PARAMS.LIMIT] || 9999
});

export const buildAttachmentGetParams = offset => ({
  [PAGE_PARAMS.LIMIT]: ATTACHMENT_PAGE_LIMIT,
  [PAGE_PARAMS.OFFSET]: offset,
  [`filter[${ATTACHMENT_FILTER_PROPERTIES.STATUS_FILTER}]`]: ATTACHMENT_STATUS_COMPLETED
});

export const buildActivityGetParams = offset => ({
  [PAGE_PARAMS.LIMIT]: ActivityQueryParams.pageLimit,
  [PAGE_PARAMS.OFFSET]: offset,
  [ACTIVITIES_PARAMS.FILTER_BY_VERB]: Object.values(ACTIVITY_VERBS).join(',')
});

export const buildExportBCFGetParams = query => {
  const filter = buildGetParams(query);
  delete filter.limit;
  delete filter.offset;
  return filter;
};

export const buildIssuesBatchGetRequest = query => buildGetParams(query);
